import React, { FC, PropsWithChildren } from 'react'
import { Navigate } from 'react-router'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Toggle } from '@mobi/component-library/Legacy/Input/Toggle'
import { useAppSelector } from '@core/Store/hooks'
import { toggleKambiLogging, toggleReactQueryDevTools } from './Store'
import { VersionInfo } from './Components/VersionInfo'
import { CookieManagement } from './Components/CookieManagement'
import { CookieInfo } from './Components/CookieInfo'
import { LocalStorageManager } from './Components/LocalStorageManager'
import { LastPaymentMethod } from '@core/Areas/Toolbox/Components/LastPaymentMethod'
import { Sentry } from '@core/Areas/Toolbox/Components/Sentry'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { GeoLocation } from './Components/GeoLocation'
import { Notifications } from './Components/Notifications'
import { FeatureToggles } from './Components/FeatureToggles'

export const Toolbox = () => {
  const toolboxElRef = React.useRef<HTMLDivElement>(null)
  const serverConfig = useServerConfig()
  const dispatch = useDispatch()

  const isReactQueryDevToolsEnabled = useAppSelector(
    state => state.toolbox.isReactQueryDevToolsEnabled
  )
  const isKambiLoggingEnabled = useAppSelector(state => state.toolbox.isKambiLoggingEnabled)

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (!serverConfig) return null
  if (serverConfig.isInternalRequest === false) return <Navigate to='/' />

  return (
    <div ref={toolboxElRef}>
      <VersionInfo />

      <h3>
        React-Query DevTools{' '}
        <Toggle
          onToggle={() => dispatch(toggleReactQueryDevTools())}
          toggledOn={isReactQueryDevToolsEnabled}
          isDisabled={false}
        />
      </h3>

      <h3>
        Kambi console logging{' '}
        <Toggle
          onToggle={() => dispatch(toggleKambiLogging())}
          toggledOn={isKambiLoggingEnabled}
          isDisabled={false}
        />
      </h3>

      <CookieInfo />
      <CookieManagement />
      <GeoLocation />
      <FeatureToggles />
      <Notifications />
      <LocalStorageManager />
      <LastPaymentMethod />
      <Sentry />
    </div>
  )
}

export const ExpandableToolboxSection: FC<{ heading: string } & PropsWithChildren> = ({
  heading,
  children,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  return (
    <>
      <ExpandableToolboxSectionStyled onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{heading}</h3>
        <div>
          <Icon name={isExpanded ? 'SolidChevronDown' : 'SolidChevronUp'} color='white' />
        </div>
      </ExpandableToolboxSectionStyled>
      <div style={{ display: 'block', margin: '1rem' }}>{isExpanded ? children : null}</div>
    </>
  )
}

const ExpandableToolboxSectionStyled = styled.section({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#2e2c30',
  marginBottom: '1rem',
  paddingRight: '0.5rem',

  '> h3': {
    margin: 0,
  },
})
